import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { logspot } from '@logspot/web';
import { LinkLogo } from '@/components/nav/link-logo';
import GoogleLogo from '@/images/google-logo.svg?react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAppForm } from '@/hooks/use-app-form';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import {
  getAuthCallbackURL,
  getOAuthResumeURL,
  redirectToAuthTarget,
} from '@/lib/auth-redirect';

const signInSchema = z.object({
  email: z.string({ message: 'Please enter your email address.' }).email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string({ message: 'Please enter your password.' }).min(8, {
    message: 'Please enter a password that is at least eight characters.',
  }),
});

type SignInFormValues = z.infer<typeof signInSchema>;

export const Route = createFileRoute('/sign-in')({
  component: SignIn,
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
  beforeLoad: async ({ context, search }) => {
    if (context.isAuthenticated()) {
      redirectToAuthTarget(search.redirect);
    }
  },
});

function SignIn() {
  const { redirect: redirectParam } = Route.useSearch();
  // Arriving from an MCP client's OAuth request, the request rides in the page
  // query. Sign-up and emailed links can't keep that query, so they carry a
  // resume link instead.
  const redirectTo = redirectParam ?? getOAuthResumeURL(window.location.search);
  const callbackURL = getAuthCallbackURL(redirectTo);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useAppForm({
    defaultValues: { email: '', password: '' } as SignInFormValues,
    validators: { onSubmit: signInSchema },
    onSubmit: async ({ value }: { value: SignInFormValues }) => {
      setIsLoading(true);
      await authClient.signIn.email(
        {
          email: value.email,
          password: value.password,
          callbackURL,
        },
        {
          onError: (ctx) => {
            setIsLoading(false);
            // Unverified email: better-auth returns 403 EMAIL_NOT_VERIFIED.
            // Send the user to the pending-verification screen where they
            // can request a fresh link.
            const code = (ctx.error as { code?: string }).code;
            const isUnverified =
              code === 'EMAIL_NOT_VERIFIED' ||
              (ctx.error.status === 403 &&
                ctx.error.message?.toLowerCase().includes('not verified'));
            if (isUnverified) {
              toast.info('Verify your email to continue', {
                description: 'We need to confirm your email address first.',
              });
              void navigate({
                to: '/verify-email',
                search: { email: value.email, redirect: redirectTo },
              });
              return;
            }
            toast.error('Sign in failed', {
              description: ctx.error.message || 'Invalid email or password.',
            });
          },
        },
      );
    },
  });


  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-sm">
          <div className="flex flex-col items-center">
            <LinkLogo />
            <h1 className="mt-12 text-3xl font-bold">
              Sign In To Your Account
            </h1>
            <p className="text-muted-foreground mt-4 font-medium">
              Web and product analytics simplified.
            </p>
          </div>

          <div className="mt-12">
            <Button
              className="w-full"
              size="lg"
              type="button"
              variant="outline"
              onClick={async () => {
                logspot.track({
                  event: 'Sign In Started',
                  metadata: { method: 'google' },
                });
                await authClient.signIn.social({
                  provider: 'google',
                  callbackURL,
                });
              }}
            >
              <GoogleLogo className="mr-2 size-5" />
              Sign In With Google
            </Button>
          </div>

          <div className="relative mt-6">
            <div className="absolute inset-0 flex items-center">
              <div className="border-border w-full border-t"></div>
            </div>

            <div className="relative flex justify-center">
              <span className="px-2"> or </span>
            </div>
          </div>

          <Tabs defaultValue="password" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Password</TabsTrigger>
              <TabsTrigger value="magic-link">Magic Link</TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <form
                className="mt-4 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <form.AppForm>
                  <form.AppField name="email">
                    {(field) => (
                      <field.Input
                        label="Email"
                        className="h-10"
                        autoComplete="email"
                        placeholder="Enter Your Email Address..."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="password">
                    {(field) => (
                      <field.Input
                        label="Password"
                        className="h-10"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Enter a Password..."
                      />
                    )}
                  </form.AppField>
                </form.AppForm>
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In With Email'}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password">
                    <Button className="px-1" variant="link" type="button">
                      Forgot Password?
                    </Button>
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="magic-link">
              <MagicLinkForm callbackURL={callbackURL} intent="sign-in" />
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Don&apos;t have an account?{' '}
              <Link to="/sign-up" search={{ redirect: redirectTo }}>
                <Button className="px-1" variant="link">
                  {' '}
                  Sign Up{' '}
                </Button>
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
